import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../context/API';
import { Merchant } from '../components/Merchants/types';

// API Response Interfaces
interface MerchantResponse {
  message?: string;
  status: number;
  data: Merchant[];
  success?: boolean;
}

interface SingleMerchantResponse {
  message?: string;
  status: number;
  data: Merchant;
  success?: boolean;
}

// Types
export type MerchantType = 'merchant' | 'manager' | 'staff';

interface CreateMerchantPayload {
  name: string;
  username: string;
  email: string;
  phone: string;
  address: string;
  password?: string;
}

interface UpdateMerchantPayload {
  id: string;
  name: string;
  username: string;
  email: string;
  phone: string;
  address: string;
}

interface MerchantsState {
  merchants: Merchant[];
  managers: Merchant[];
  staff: Merchant[];
  loading: {
    merchants: boolean;
    managers: boolean;
    staff: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  error: string | null;
}

const initialState: MerchantsState = {
  merchants: [],
  managers: [],
  staff: [],
  loading: {
    merchants: false,
    managers: false,
    staff: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
};

// Helper function to get API endpoint based on type
const getEndpoint = (type: MerchantType, id?: string): string => {
  const baseEndpoint = type === 'merchant' ? 'merchant' : type === 'manager' ? 'manager' : 'staff';
  return id ? `${baseEndpoint}/${id}` : baseEndpoint;
};

// Async thunks
export const fetchMerchants = createAsyncThunk<Merchant[], void, { rejectValue: string }>(
  'merchants/fetchMerchants',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get<MerchantResponse>('merchant', {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });
      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch merchants'
      );
    }
  }
);

export const fetchManagers = createAsyncThunk<Merchant[], void, { rejectValue: string }>(
  'merchants/fetchManagers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get<MerchantResponse>('manager/all', {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });
      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch managers'
      );
    }
  }
);

export const fetchStaff = createAsyncThunk<Merchant[], void, { rejectValue: string }>(
  'merchants/fetchStaff',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get<MerchantResponse>('staff/all', {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });
      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch staff'
      );
    }
  }
);

interface CreateMerchantParams {
  type: MerchantType;
  payload: CreateMerchantPayload;
}

export const createMerchant = createAsyncThunk<
  Merchant,
  CreateMerchantParams,
  { rejectValue: string }
>('merchants/createMerchant', async ({ type, payload }, { rejectWithValue }) => {
  try {
    const endpoint = getEndpoint(type);
    const response = await API.post<SingleMerchantResponse>(endpoint, payload, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });

    if (response.data.status === 200) {
      return response.data.data;
    } else {
      return rejectWithValue(response.data.message || 'Creation failed');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to create merchant'
    );
  }
});

interface UpdateMerchantParams {
  type: MerchantType;
  payload: UpdateMerchantPayload;
}

export const updateMerchant = createAsyncThunk<
  Merchant,
  UpdateMerchantParams,
  { rejectValue: string }
>('merchants/updateMerchant', async ({ type, payload }, { rejectWithValue }) => {
  try {
    const endpoint = getEndpoint(type, payload.id);
    const updatePayload = {
      name: payload.name,
      username: payload.username,
      email: payload.email,
      phone: payload.phone,
      address: payload.address,
    };

    const response = await API.put<SingleMerchantResponse>(endpoint, updatePayload, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });

    if (response.data.status === 200) {
      return response.data.data;
    } else {
      return rejectWithValue(response.data.message || 'Update failed');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to update merchant'
    );
  }
});

interface DeleteMerchantParams {
  type: MerchantType;
  id: string;
}

export const deleteMerchant = createAsyncThunk<
  string,
  DeleteMerchantParams,
  { rejectValue: string }
>('merchants/deleteMerchant', async ({ type, id }, { rejectWithValue }) => {
  try {
    const endpoint = getEndpoint(type, id);
    const response = await API.delete(endpoint, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });

    if (response.data.status === 200) {
      return id;
    } else {
      return rejectWithValue(response.data.message || 'Delete failed');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to delete merchant'
    );
  }
});

const merchantsSlice = createSlice({
  name: 'merchants',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch merchants
      .addCase(fetchMerchants.pending, (state) => {
        state.loading.merchants = true;
        state.error = null;
      })
      .addCase(fetchMerchants.fulfilled, (state, action) => {
        state.loading.merchants = false;
        state.merchants = action.payload;
      })
      .addCase(fetchMerchants.rejected, (state, action) => {
        state.loading.merchants = false;
        state.error = action.payload as string;
      })
      // Fetch managers
      .addCase(fetchManagers.pending, (state) => {
        state.loading.managers = true;
        state.error = null;
      })
      .addCase(fetchManagers.fulfilled, (state, action) => {
        state.loading.managers = false;
        state.managers = action.payload;
      })
      .addCase(fetchManagers.rejected, (state, action) => {
        state.loading.managers = false;
        state.error = action.payload as string;
      })
      // Fetch staff
      .addCase(fetchStaff.pending, (state) => {
        state.loading.staff = true;
        state.error = null;
      })
      .addCase(fetchStaff.fulfilled, (state, action) => {
        state.loading.staff = false;
        state.staff = action.payload;
      })
      .addCase(fetchStaff.rejected, (state, action) => {
        state.loading.staff = false;
        state.error = action.payload as string;
      })
      // Create merchant
      .addCase(createMerchant.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createMerchant.fulfilled, (state, action) => {
        state.loading.create = false;
        const type = action.meta.arg.type;
        if (type === 'merchant') {
          state.merchants.push(action.payload);
        } else if (type === 'manager') {
          state.managers.push(action.payload);
        } else {
          state.staff.push(action.payload);
        }
      })
      .addCase(createMerchant.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload as string;
      })
      // Update merchant
      .addCase(updateMerchant.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateMerchant.fulfilled, (state, action) => {
        state.loading.update = false;
        const type = action.meta.arg.type;
        const updatedMerchant = action.payload;
        if (type === 'merchant') {
          const index = state.merchants.findIndex((m) => m.id === updatedMerchant.id);
          if (index !== -1) state.merchants[index] = updatedMerchant;
        } else if (type === 'manager') {
          const index = state.managers.findIndex((m) => m.id === updatedMerchant.id);
          if (index !== -1) state.managers[index] = updatedMerchant;
        } else {
          const index = state.staff.findIndex((m) => m.id === updatedMerchant.id);
          if (index !== -1) state.staff[index] = updatedMerchant;
        }
      })
      .addCase(updateMerchant.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload as string;
      })
      // Delete merchant
      .addCase(deleteMerchant.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteMerchant.fulfilled, (state, action) => {
        state.loading.delete = false;
        const type = action.meta.arg.type;
        const id = action.payload;
        if (type === 'merchant') {
          state.merchants = state.merchants.filter((m) => m.id !== id);
        } else if (type === 'manager') {
          state.managers = state.managers.filter((m) => m.id !== id);
        } else {
          state.staff = state.staff.filter((m) => m.id !== id);
        }
      })
      .addCase(deleteMerchant.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = merchantsSlice.actions;
export default merchantsSlice.reducer;